import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { BellRing, Check, ChevronLeft, Compass, PenLine, Phone, X } from 'lucide-react';
import { cn } from 'lib/utils';
import { Button } from 'components/ui/button';
import { ApiError } from 'utils/unwrapResponse';
import { apiErrorMessage } from 'utils/errorMessage';
import { formatDateTime, formatDay } from 'utils/formatDate';
import { formatDuration, formatElapsed } from 'utils/elapsed';
import {
  interventionDay,
  refusalReasonLabel,
} from 'pages/AdminIssues/components/upcomingAssignment';
import {
  calendarDay,
  dayAndTime,
  isDeadlinePassed,
  parisDateTime,
  shortDay,
  weekdayDay,
} from 'utils/interventionDate';
import { moneyText, type Money } from 'utils/money';
import { activeLocale } from 'i18n';
import { windowHours, windowLabel } from 'constants/timeWindows';
import DeferredSection from 'pages/AdminTechnician/DeferredSection';
import InfoRow from 'components/InfoRow';
import {
  hasUnseenReminder,
  requestVariant,
  type RequestVariant,
  useAcceptMyInterventionRequest,
  useDeclineMyInterventionRequest,
  useMarkSeen,
} from 'hooks/useMyInterventionRequests';
import type { AcceptSlot, MyInterventionRequest } from 'api/myInterventionRequestsApi';
import type { InterventionRequest, RefusalReasonCode } from 'api/interventionRequestsApi';
import AcceptSheet from './AcceptSheet';
import DeclineSheet from './DeclineSheet';
import { statusTint, TINT } from './requestVisuals';

/**
 * One request, and the two answers. The card constants and the status pill live here rather than
 * in a file of their own because the list imports them from this module — the other direction
 * would be a cycle, and the lot is deliberately four files.
 */

export const CARD = 'rounded-2xl border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900';
/** 44 px, the smallest target a thumb hits reliably — the mock-up draws 38. */
const TAP = 'flex h-11 w-11 shrink-0 items-center justify-center rounded-xl';
export const HINT = 'text-sm leading-relaxed text-zinc-500 dark:text-zinc-400';
/** An em dash and never an empty line: a blank value would read as "not applicable". */
export const DASH = '—';

/** R16 — « 150 € HT → 180 € HT » from the last voided line, only when it was voided after this request was sent. */
const moneyChange = (money: Money | null | undefined, sentAt: string): string | null =>
  money?.previous && Date.parse(money.previous.voidedAt) > Date.parse(sentAt)
    ? `${moneyText(money.previous, activeLocale())} → ${moneyText(money, activeLocale())}`
    : null;

/**
 * His own words, and not the back office's: « En attente de réponse » describes somebody waiting
 * on him, which is not what he reads on his own request. A `switch` of literal keys — a key built
 * by concatenation is invisible to the parity check.
 */
const statusLabel = (t: TFunction, status: RequestVariant): string => {
  switch (status) {
    case 'accepted':
      return t('Accepted');
    case 'declined':
      return t('Declined');
    case 'reminded':
      return t('Reminded');
    case 'overdue':
      return t('Deadline passed');
    default:
      return t('New request');
  }
};

/** One pill for the list and the detail — the mock-up's tints, dot included. */
export const StatusPill = ({ status }: { status: RequestVariant }) => {
  const { t } = useTranslation();
  const tint =
    status === 'reminded' ? TINT.amber : status === 'overdue' ? TINT.zinc : statusTint(status);
  return (
    <span
      className={cn(
        'inline-flex h-5 shrink-0 items-center gap-[5px] rounded-full pl-[7px] pr-2 text-[11px] font-medium',
        tint.bg,
        tint.fg
      )}
    >
      <span className={cn('h-[5px] w-[5px] shrink-0 rounded-full', tint.dot)} aria-hidden="true" />
      {statusLabel(t, status)}
    </span>
  );
};

/**
 * Why an answer failed — three families, **never collapsed into one** (D32).
 *
 * The status is read **before** the error code: a 401 rendered as « vous avez déjà répondu » would
 * make him believe his answer went out (R-053). The two 409 codes then keep their own sentences —
 * "already answered" and "given to somebody else" are not the same event (R-050) — and a thrown
 * value that is not an `ApiError` never reached the server at all, so it says so and stays
 * retryable.
 */
const answerError = (t: TFunction, error: unknown): string => {
  if (!(error instanceof ApiError)) {
    return t('The answer could not be sent. Check your connection, then try again.');
  }
  if (error.status === 401 || error.status === 403) {
    return t('Your session has expired. Sign in again, then answer.');
  }
  if (error.status === 404) return t('This request is no longer available.');
  if (error.errorCode) return apiErrorMessage(t, error);
  return t('The answer could not be sent. Try again in a moment.');
};

/** The acceptance's own codes (CA4, PR3 ruling, F14); everything else is the shared families. */
const acceptError = (t: TFunction, error: unknown, earliest: string | null): string => {
  if (error instanceof ApiError && error.errorCode === 'INTERVENTION_REQUEST_MISSION_TAKEN') {
    return t('Another technician has already accepted this mission.');
  }
  if (error instanceof ApiError && error.errorCode === 'INTERVENTION_REQUEST_BEFORE_START' && earliest) {
    return t('No visit before {{start}}', { start: shortDay(earliest) });
  }
  if (error instanceof ApiError && error.errorCode === 'VALIDATION_FAILED') {
    return t('This day or time has already passed. Pick a later slot.');
  }
  return answerError(t, error);
};

/**
 * His other missions **already accepted** on the day he picks (D68) — their chosen day, else the
 * ticket's — pure arithmetic on the list the screen already holds. `calendarDay` and never a
 * `slice`: a day that does not exist is « no date » here as everywhere (D65).
 */
export const acceptedSameDay = (
  requests: MyInterventionRequest[],
  current: MyInterventionRequest,
  day: string
): number =>
  requests.filter(
    (other) =>
      other.id !== current.id &&
      other.status === 'accepted' &&
      calendarDay(other.chosenDate ?? other.interventionDate) === day
  ).length;

interface RequestDetailProps {
  request: MyInterventionRequest;
  /** Missions already accepted on a day he picks, this one excluded — counted by the list, the
   *  only holder of his other requests. `0` is silent. */
  sameDayMissions: (day: string) => number;
  /** The chevron and the pending footer: back to the list, filter untouched. */
  onBack: () => void;
  /** After an answer: back to the list, chip filter back to « Toutes ». */
  onDone: () => void;
  /** Hold this row while an accept is in flight, and after a lost race (CA4): the refetch drops
   *  a superseded row, possibly before the error reaches this screen. */
  onHold?: (hold: boolean) => void;
}

const RequestDetail = ({ request, sameDayMissions, onBack, onDone, onHold }: RequestDetailProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const accept = useAcceptMyInterventionRequest();
  const decline = useDeclineMyInterventionRequest();
  const { mutate: markSeen } = useMarkSeen();

  const [declining, setDeclining] = useState(false);
  const [accepting, setAccepting] = useState(false);
  /** Keys the sheet's form, so each opening starts fresh (see AcceptSheet). */
  const [acceptSession, setAcceptSession] = useState(0);
  // Snapshot at opening: the seen write refetches the list and must not hide the banner he reads.
  const [reminded] = useState(() => request.status === 'pending' && hasUnseenReminder(request));
  /** The row the server sent back, which is what carries the delay it measured. */
  const [answered, setAnswered] = useState<InterventionRequest | null>(null);

  // Fire and forget (E15): clears the amber badge, never surfaces a failure.
  useEffect(() => {
    markSeen(request.id);
  }, [markSeen, request.id]);

  const day = interventionDay(request.interventionDate);
  /** The success screen only: the list and the detail row show the time on its own. */
  const dayWithTime = dayAndTime(day, request.interventionTime);
  const pending = request.status === 'pending';
  const busy = accept.isPending || decline.isPending;
  const deadline = request.deadlineDate ?? null;
  // Pre-L6 rows have no upper bound (CA16), so only a real deadline can pass.
  const deadlinePassed = pending && isDeadlinePassed(deadline);
  const windowName = windowLabel(t, request.timeWindow);
  const hours = windowHours(request.timeWindow);
  /** The deadline, else the ticket's day on a pre-L6 row (CA16). */
  const deadlineDay = deadline ? formatDay(deadline) : day || DASH;
  const earliest = calendarDay(request.earliestDate);
  // L7 window (F14): a fixed day when both ends meet; a pre-L7 row keeps the deadline line.
  const dayWindow = earliest && deadline ? { fixed: earliest === deadline, start: earliest, end: deadline } : null;
  const tariffText = request.tariff ? moneyText(request.tariff, activeLocale()) : null;
  const feeText = request.travelFee ? moneyText(request.travelFee, activeLocale()) : null;
  const tariffChange = moneyChange(request.tariff, request.sentAt);
  const feeChange = moneyChange(request.travelFee, request.sentAt);
  const acceptFailure = accept.error ? acceptError(t, accept.error, earliest) : null;

  const confirmAccept = (slot: AcceptSlot) => {
    if (busy) return;
    onHold?.(true);
    accept.mutate(
      { id: request.id, ...slot },
      {
        onSuccess: (updated) => {
          onHold?.(false);
          setAccepting(false);
          setAnswered(updated);
        },
        // Only a lost race keeps the hold; any other failure lets a vanished row go.
        onError: (error) =>
          onHold?.(error instanceof ApiError && error.errorCode === 'INTERVENTION_REQUEST_MISSION_TAKEN'),
      }
    );
  };

  const confirmDecline = (refusalReasonCode: RefusalReasonCode, refusalReason: string | null) => {
    if (busy) return;
    decline.mutate(
      { id: request.id, refusalReasonCode, refusalReason },
      {
        onSuccess: (updated) => {
          setDeclining(false);
          setAnswered(updated);
        },
      }
    );
  };

  // Success is a screen, not a silence: what he answered, on which mission, and how long he took.
  if (answered) {
    return (
      <div className="flex flex-col items-center gap-3 py-10 text-center">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400">
          <Check className="h-6 w-6" aria-hidden="true" />
        </span>
        <p className="text-[15px] font-semibold text-zinc-900 dark:text-zinc-50">
          {answered.status === 'accepted' ? t('Request accepted') : t('Refusal recorded')}
        </p>
        <p className={cn('max-w-[300px]', HINT)}>
          {answered.status === 'accepted' && answered.chosenDate && answered.chosenTime
            ? t('{{store}} · {{day}} at {{time}}', {
                store: request.store || DASH,
                day: formatDay(answered.chosenDate),
                time: answered.chosenTime,
              })
            : [request.store || DASH, request.requestType, dayWithTime].filter(Boolean).join(' · ')}
        </p>
        {answered.status === 'accepted' && (
          <p className={cn('max-w-[300px]', HINT)}>{t('The GPS team is informed. This answer is final.')}</p>
        )}
        {answered.responseDelayS !== null && (
          <p className="text-xs text-zinc-400 dark:text-zinc-500">
            {t('Answered in {{duration}}', { duration: formatDuration(answered.responseDelayS) })}
          </p>
        )}
        <Button className="mt-4 h-12 w-full max-w-sm text-sm" onClick={onDone}>
          {t('Back to my requests')}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 pb-4">
      <div className="flex items-center gap-1 border-b border-zinc-200 pb-3 dark:border-zinc-800">
        {/* Kept because it works in a browser — and never the only way out: on iOS the WebView's
            back-forward gesture swallows every touch within ~50 pt of the left edge, which is
            where this chevron sits. The footer below carries the exit that survives that (D33). */}
        <button
          type="button"
          onClick={onBack}
          aria-label={t('Back')}
          className={cn(TAP, '-ml-2 text-zinc-900 dark:text-zinc-100')}
        >
          <ChevronLeft className="h-6 w-6" aria-hidden="true" />
        </button>
        <div className="min-w-0 flex-1">
          <p className="truncate text-base font-semibold text-zinc-900 dark:text-zinc-50">
            {[request.store || DASH, request.storeCity].filter(Boolean).join(' · ')}
          </p>
          <p className="truncate font-mono text-xs text-zinc-500 dark:text-zinc-400">
            {request.issueKey}
          </p>
        </div>
        <StatusPill status={requestVariant(request)} />
      </div>

      <section className={cn(CARD, 'p-4')}>
        <p className="text-[15px] font-semibold text-zinc-900 dark:text-zinc-50">
          {request.store || DASH}
        </p>
        <p className="mt-0.5 text-[13px] text-zinc-500 dark:text-zinc-400">
          {[request.storeCity, request.requestType].filter(Boolean).join(' · ') || DASH}
        </p>
        <p className="mt-2 text-xs text-zinc-400 dark:text-zinc-500">
          {t('Received {{when}}', { when: formatElapsed(request.elapsedSeconds) })}
          {request.serviceDeskName ? ` · ${request.serviceDeskName}` : ''}
        </p>
      </section>

      {reminded && request.lastReminderAt && (
        <div className="flex gap-2.5 rounded-xl border border-amber-200 bg-amber-50 p-3 text-[13px] leading-relaxed text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300">
          <BellRing className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <div>
            <p className="font-medium">
              {t('Reminded on {{date}} · answer before {{deadline}}', {
                date: parisDateTime(request.lastReminderAt).date,
                deadline: deadlineDay,
              })}
            </p>
            <p>{t('The GPS team is waiting for your answer.')}</p>
          </div>
        </div>
      )}

      {(tariffChange || feeChange) && (
        <div className="flex gap-2.5 rounded-xl border border-amber-200 bg-amber-50 p-3 text-[13px] leading-relaxed text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300">
          <PenLine className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <div>
            <p className="font-medium">{t('Request changed')}</p>
            {tariffChange && <p>{t('Tariff: {{amount}}', { amount: tariffChange })}</p>}
            {feeChange && <p>{t('Travel fee: {{amount}}', { amount: feeChange })}</p>}
          </div>
        </div>
      )}

      {/* Always shown (CA2); a pre-L6 row falls back to the ticket's date, else says none (CA16). */}
      <section className={cn(CARD, 'p-4')}>
        <div className="mb-2 flex items-center justify-between gap-2">
          <h2 className="text-sm font-semibold text-zinc-900 dark:text-zinc-50">{t('To schedule')}</h2>
          {dayWindow && (
            <span className="rounded-md bg-sky-50 px-2 py-0.5 text-[11px] font-medium text-sky-800 dark:bg-sky-950 dark:text-sky-200">
              {dayWindow.fixed ? t('Fixed day') : t('Range')}
            </span>
          )}
        </div>
        <p className="text-[14px] font-medium text-zinc-900 dark:text-zinc-50">
          {dayWindow?.fixed
            ? t('Fixed day: {{day}}', { day: weekdayDay(dayWindow.end) })
            : dayWindow
              ? t('Possible days: from {{start}} to {{end}}', {
                  start: shortDay(dayWindow.start),
                  end: shortDay(dayWindow.end),
                })
              : deadline || day
                ? t('Deadline: at the latest {{day}}', { day: deadlineDay })
                : t('Deadline not set')}
          {deadlinePassed && (
            <span className="text-rose-700 dark:text-rose-400">{` — ${t('passed')}`}</span>
          )}
        </p>
        {windowName && hours && (
          <p className={cn('mt-1', HINT)}>
            {t('Preferred window: {{window}} ({{hours}})', {
              window: windowName,
              hours,
            })}
          </p>
        )}
        {request.indicativeTime && (
          <p className={cn('mt-1', HINT)}>
            {t('Indicative time: {{time}}', { time: request.indicativeTime })}
          </p>
        )}
        {tariffText && (
          <p className="mt-1 text-[14px] text-zinc-900 dark:text-zinc-50">
            {t('Tariff: {{amount}}', { amount: tariffText })}
          </p>
        )}
        {feeText && (
          <p className="mt-1 text-[14px] text-zinc-900 dark:text-zinc-50">
            {t('Travel fee: {{amount}}', { amount: feeText })}
          </p>
        )}
      </section>

      <section className={cn(CARD, 'overflow-hidden')}>
        <h2 className="px-4 pb-3 pt-4 text-sm font-semibold text-zinc-900 dark:text-zinc-50">
          {t('Intervention')}
        </h2>
        <InfoRow label={t('Client')} value={request.client || DASH} />
        <InfoRow label={t('Point of sale')} value={request.store || DASH} />
        <InfoRow label={t('City')} value={request.storeCity || DASH} />
        <InfoRow label={t('Address')} value={request.storeAddress || DASH} />
        <InfoRow label={t('Request type')} value={request.requestType || DASH} />
        {request.brand && <InfoRow label={t('Brand')} value={request.brand} />}
        {request.furniture && <InfoRow label={t('Furniture')} value={request.furniture} />}
        {/* With a deadline, the ticket date is that deadline — the schedule card already says it. */}
        {!deadline && <InfoRow label={t('Date')} value={day || DASH} />}
        {/* Absent on a desk with no time field (D75), and with a deadline: the hour is the technician's. */}
        {!deadline && request.interventionTime && (
          <InfoRow label={t('Intervention time')} value={request.interventionTime} />
        )}
      </section>

      <section className={cn(CARD, 'p-4')}>
        <h2 className="text-sm font-semibold text-zinc-900 dark:text-zinc-50">{t('Description')}</h2>
        <p className={cn('mt-2 whitespace-pre-line', HINT)}>{request.description || DASH}</p>
      </section>

      {/* No fallback text: silence is honest when nobody wrote a note, a placeholder is not. */}
      {request.customMessage?.trim() && (
        <section className={cn(CARD, 'p-4')}>
          <h2 className="text-sm font-semibold text-zinc-900 dark:text-zinc-50">
            {t('Useful information')}
          </h2>
          <p className={cn('mt-2 whitespace-pre-line', HINT)}>{request.customMessage}</p>
        </section>
      )}

      {request.storePhone ? (
        <section className={cn(CARD, 'p-4')}>
          <h2 className="text-sm font-semibold text-zinc-900 dark:text-zinc-50">
            {t('Store phone')}
          </h2>
          <a
            href={`tel:${request.storePhone.replace(/\s+/g, '')}`}
            aria-label={t('Call the store')}
            className="mt-2 flex h-11 items-center gap-2 text-[15px] font-medium text-zinc-900 dark:text-zinc-50"
          >
            <Phone className="h-4 w-4 shrink-0 text-zinc-400 dark:text-zinc-500" aria-hidden="true" />
            {request.storePhone}
          </a>
        </section>
      ) : (
        <DeferredSection
          icon={Phone}
          title={t('Store phone')}
          explanation={t(
            'No phone number available for this store. Ask the back office if you need to call ahead.'
          )}
        />
      )}

      {/* He cannot guess it, and it is a legitimate ground for refusing (D22). */}
      {request.sentOutsidePerimeter && (
        <p className="flex gap-2.5 rounded-xl border border-amber-200 bg-amber-50 p-3 text-[13px] leading-relaxed text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300">
          <Compass className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          {t(
            'This mission is outside the area you usually cover. You may decline for that reason.'
          )}
        </p>
      )}

      {!pending && (
        <section className={cn(CARD, 'p-4')}>
          <h2 className="text-sm font-semibold text-zinc-900 dark:text-zinc-50">
            {t('Your answer')}
          </h2>
          <p className="mt-2 text-[13px] font-medium text-zinc-900 dark:text-zinc-50">
            {request.status === 'accepted' ? t('You accepted this request.') : t('You declined this request.')}
          </p>
          {request.status === 'accepted' && request.chosenDate && request.chosenTime && (
            <p className="mt-1 text-[13px] text-zinc-900 dark:text-zinc-50">
              {t('Your visit: {{day}} at {{time}}', {
                day: formatDay(request.chosenDate),
                time: request.chosenTime,
              })}
            </p>
          )}
          {request.status === 'declined' && request.refusalReasonCode && (
            <p className={cn('mt-1', HINT)}>{refusalReasonLabel(t, request.refusalReasonCode)}</p>
          )}
          {request.refusalReason && <p className={cn('mt-1', HINT)}>{request.refusalReason}</p>}
          <p className="mt-2 text-xs text-zinc-400 dark:text-zinc-500">
            {[
              request.respondedAt ? formatDateTime(request.respondedAt) : null,
              request.responseDelayS !== null
                ? t('Answered in {{duration}}', { duration: formatDuration(request.responseDelayS) })
                : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
          {/* The lock is a `WHERE status = 'pending'`: an answer cannot be taken back, by
              construction. An unspoken dead end is a defect, so the card says it — and points at
              the one lever he does have, without promising a cancellation the model forbids
              (B-07: a declared unavailability never touches an accepted mission). */}
          <p className={cn('mt-3', HINT)}>{t('This answer is final and cannot be changed from the application.')}</p>
          {request.status === 'accepted' && (
            <>
              <p className={cn('mt-2', HINT)}>
                {t(
                  'If something prevents you from going, declare an unavailability from your profile. That does not cancel this intervention — the back office will contact you to reorganise it.'
                )}
              </p>
              <Button
                variant="outline"
                className="mt-3 h-11 w-full text-sm"
                onClick={() => navigate('/profile')}
              >
                {t('Open my profile')}
              </Button>
            </>
          )}
        </section>
      )}

      {/* Sticky, and full-bleed over the page's own padding so nothing shows through under it. */}
      <div className="sticky bottom-0 -mx-4 flex flex-col gap-2.5 border-t border-zinc-200 bg-white px-4 pb-[30px] pt-3 dark:border-zinc-800 dark:bg-zinc-950">
        {deadlinePassed && (
          <p className={cn(HINT, 'text-center')}>{t('The deadline has passed: contact the GPS team.')}</p>
        )}
        {pending && (
          <div className="flex gap-2.5">
            <Button
              variant="outline"
              className="h-12 flex-1 gap-2 rounded-xl border-rose-700 bg-white text-[14.5px] font-semibold text-rose-700 shadow-none hover:bg-rose-50 hover:text-rose-700 active:bg-rose-50 dark:border-rose-400 dark:bg-zinc-900 dark:text-rose-400 dark:hover:bg-rose-950 dark:hover:text-rose-400 dark:active:bg-rose-950 [&_svg]:size-[17px]"
              disabled={busy}
              onClick={() => setDeclining(true)}
            >
              <X aria-hidden="true" />
              {t('Decline')}
            </Button>
            <Button
              className="h-12 flex-1 gap-2 rounded-xl border border-primary text-[14.5px] font-semibold shadow-none [&_svg]:size-[18px]"
              disabled={busy || deadlinePassed}
              onClick={() => {
                setAcceptSession((session) => session + 1);
                setAccepting(true);
              }}
            >
              <Check aria-hidden="true" />
              {t('Accept')}
            </Button>
          </div>
        )}
        {/* Full width on purpose: the iOS dead zone is the leftmost ~50 pt, and a button that
            spans the screen is always tappable past it. Offered on `pending` too — the mock-up
            only draws it once answered, which leaves the new request with the dead chevron alone. */}
        <Button variant="ghost" className="h-11 w-full text-sm" onClick={pending ? onBack : onDone}>
          {t('Back to my requests')}
        </Button>
      </div>

      <AcceptSheet
        open={accepting}
        session={acceptSession}
        request={request}
        pending={accept.isPending}
        error={acceptFailure}
        retryable={!!accept.error && !(accept.error instanceof ApiError)}
        taken={accept.error instanceof ApiError && accept.error.errorCode === 'INTERVENTION_REQUEST_MISSION_TAKEN'}
        tariffText={tariffText}
        feeText={feeText}
        sameDayMissions={sameDayMissions}
        onEdit={() => accept.error && accept.reset()}
        onClose={() => {
          accept.reset();
          setAccepting(false);
        }}
        onBackToList={onDone}
        onConfirm={confirmAccept}
      />

      <DeclineSheet
        open={declining}
        pending={decline.isPending}
        error={decline.error ? answerError(t, decline.error) : null}
        tariffText={tariffText}
        feeText={feeText}
        onClose={() => setDeclining(false)}
        onConfirm={confirmDecline}
      />
    </div>
  );
};

export default RequestDetail;
